import React, { useState, useEffect, useCallback } from "react";
import {
  ThemeProvider,
  CssBaseline,
  Box,
  Alert,
  IconButton,
  Typography,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { theme } from "../../theme";
import { useOutlookPlugin } from "../../hooks/useOutlookPlugin";
import { EmailAutomationPresentationComponent } from "./EmailAutomationPresentationComponent";
import type { EmailAutomationSection } from "@services/emailAutomationPipeline/types";
import type { GetEmailAutomationResponse } from "../../api/types";
import { useSessionRecording } from "@/components/useSessionRecording";
import { SearchPanel } from "./SearchPanel";

interface EmailAutomationPageProps {
  onBack: () => void;
}

export const EmailAutomationPage: React.FC<EmailAutomationPageProps> = ({
  onBack,
}) => {
  const { currentMessageId, currentSharedInbox, isReady, apiClient } = useOutlookPlugin();
  useSessionRecording("6jyi7b/mark3outlookplugin");
  const [sections, setSections] = useState<EmailAutomationSection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const fetchEmailAutomation = useCallback(
    (messageId: string, sharedInbox?: string | null) => {
      let url = `/email-automation/result?officeMessageId=${encodeURIComponent(messageId)}`;
      if (sharedInbox) {
        url += `&sharedInbox=${encodeURIComponent(sharedInbox)}`;
      }
      return apiClient.get<GetEmailAutomationResponse>(url);
    },
    [apiClient]
  );

  useEffect(() => {
    if (!isReady || !currentMessageId) {
      setSections([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchEmailAutomation(
          currentMessageId,
          currentSharedInbox,
        );
        if (response.status === "ok" && response.data) {
          setSections(response.data.sections);
        } else if (response.status === "not_found") {
          setSections([]);
        } else if (response.status === "error") {
          setError(response.error || "Failed to load email automation data");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentMessageId, currentSharedInbox, isReady, fetchEmailAutomation]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box>
        {/* Search button to open search panel */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={0.5}
          sx={{ px: 1, pt: 1, cursor: "pointer" }}
          onClick={() => setSearchOpen(true)}
        >
          <Typography variant="body2" color="text.secondary">Search</Typography>
          <IconButton size="small">
            <SearchIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* No message selected */}
        {!currentMessageId && !loading && (
          <Box sx={{ p: 2 }}>
            <Alert severity="info">Select an email.</Alert>
          </Box>
        )}

        {/* Render presentation component when message is selected */}
        {currentMessageId && (
          <EmailAutomationPresentationComponent
            sections={sections}
            loading={loading}
            error={error}
            emptyMessage="No data available for this email."

          />
        )}

      </Box>

      <SearchPanel open={searchOpen} onClose={() => setSearchOpen(false)} />
    </ThemeProvider>
  );
};
